import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { auditTime, distinctUntilChanged, filter, firstValueFrom, map, Observable, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { pick } from 'lodash';

import { ActivityDateData, HotelResultData, ImageResultData, ItineraryParam, SearchData, UpdateOrderItineraries } from '@hiptraveler/data-access/api';
import { AuthState } from '@hiptraveler/data-access/auth';
import { UserState } from '@hiptraveler/data-access/user';
import { ProfileState } from '@hiptraveler/data-access/profile';
import { BrandState } from '@hiptraveler/data-access/brand';
import { ItineraryAction, ItineraryActivityPosition, ItineraryState } from '@hiptraveler/data-access/itinerary';
import { getAdventuresQuery, getFoodsQuery, getHotelsQuery, SearchAction } from '@hiptraveler/data-access/search';
import { DialogType, SearchResultDialogActionService } from '@hiptraveler/dialogs/search-result-dialog';
import { PromptDialogActionService, PromptDialogStateService } from '@hiptraveler/dialogs/prompt-dialog';
import { AuthDialogActionService } from '@hiptraveler/dialogs/auth-dialog';
import { ComposePrivateNoteDialogActionService } from '@hiptraveler/dialogs/compose-private-note-dialog';
import { SnackbarService } from '@hiptraveler/snackbar';
import { ActivityDate, AppListenerService, NavbarControlStateService, ScrollListenerService, SearchLocationService, SearchPageControlStateService, currentLang, getWindowRef, pendingAuthProcessKey, removeCircularReferences, updateOrderItinerariesKey } from '@hiptraveler/common';
import { ComponentStateService, exitFocusModeGlobalListenerKey, ItineraryStateService, preventRemoveKey, preventUpdateFormStateByPlaceResult, SearchParam } from '@hiptraveler/features/itinerary';

@Injectable()
export class ActivityDayListService {

  itineraryType: ItineraryParam = 'itinerary';
  #savedChanges: { [key:number]: boolean } = {};
  authHandle$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store,
    private appListener: AppListenerService,
    private navbarControl: NavbarControlStateService,
    private searchLocation: SearchLocationService,
    private searchPageControl: SearchPageControlStateService,
    private scrollListener: ScrollListenerService,
    private authDialog: AuthDialogActionService,
    private searchResultDialog: SearchResultDialogActionService,
    private promptDialog: PromptDialogActionService,
    private promptDialogState: PromptDialogStateService,
    private composePrivateNote: ComposePrivateNoteDialogActionService,
    private componentState: ComponentStateService,
    private stateService: ItineraryStateService,
    private snackbar: SnackbarService
  ) {
    sessionStorage.removeItem(updateOrderItinerariesKey);
  }

  /**
   * Returns activity date map data in array
   */
  get actDate$(): Observable<ActivityDateData[] | null> {
    return this.store.select(ItineraryState.actDateArr).pipe(
      distinctUntilChanged((p, c) => JSON.stringify(p, removeCircularReferences()) === JSON.stringify(c, removeCircularReferences()))
    );
  }

  globalSignalObserver(): void {
    this.appListener.globalSignalListener(exitFocusModeGlobalListenerKey).pipe(
      switchMap(() => this.searchPageControl.previousDay$.pipe(filter(Boolean), take(1)))
    ).subscribe((day: number) => {
      
      const activity = this.store.selectSnapshot(ItineraryState.actDateMap)?.[day] as ActivityDateData;
      const previousUrl = this.appListener.previousUrl;
      const param = previousUrl?.replace(`/${currentLang()}/`, '')?.split('/')?.[0] as SearchParam;
      const composePage = getWindowRef().location.pathname.includes(`/${currentLang()}/compose/`);

      if (!activity || !previousUrl || !composePage) return;

      const activityDate = this.setActivityDateByActivityData(activity);
      this.navigate(activityDate, param);
    });
  }

  activityDayListUiObserver(): void {

    this.scrollListener.scrollPosition$.pipe(
      auditTime(350)
    ).subscribe((scrollTop: number) => {

      const activityData = this.componentState.subscription$$.value?.activeDayConfig || [];

      function getActivityDay(scrollTop: number): any {
        for (let i = 0; i < activityData.length; i++) {
          let currentActivity = activityData[i];
          let nextActivity = activityData[i + 1];
  
          if (!nextActivity) {
            if (scrollTop >= currentActivity.position) {
                return currentActivity.activityDay;
            }
          }
  
          if (scrollTop >= currentActivity.position && scrollTop < nextActivity.position) {
            return currentActivity.activityDay;
          }
        }
      }

      const value = getActivityDay(scrollTop);
      value && this.componentState.patch({ activeDay: value });
    });
  }

  /**
   * Handles the movement of an itinerary activity position.
   * 
   * @param data - Information about the moved itinerary activity.
   */
  async positionMoved(data: ItineraryActivityPosition): Promise<void> {

    if (!this.#savedChanges?.[data.day]) {
      this.snackbar.open({ message: `Please don't forget to click the "Save" button to save your changes!` });
      this.#savedChanges[data.day] = true
    }

    await firstValueFrom(this.store.dispatch(new ItineraryAction.MoveItineraryActivityPosition(data)));

    const updateOrderItineraries: UpdateOrderItineraries[] = JSON.parse(sessionStorage.getItem(updateOrderItinerariesKey) || '[]');
    const value: UpdateOrderItineraries = {
      day: data?.day || -1,
      imgArray: this.store.selectSnapshot(ItineraryState.actDateMap)?.[data.day]?.imgArray?.map(e => e.id) || []
    };
    
    const existingItinerary = updateOrderItineraries.find(e => e.day === data?.day);
    if (existingItinerary) {
      existingItinerary.imgArray = value.imgArray;
    } else {
      updateOrderItineraries.push(value);
    }
    sessionStorage.setItem(updateOrderItinerariesKey, JSON.stringify(updateOrderItineraries));
  }

  /**
   * Navigates to a different page based on the selected activity and type.
   * 
   * @param activity - Data about the selected activity.
   * @param selection - The type of activity (thingstodo, hotels, or foodanddrink).
   */
  async navigate(activity: ActivityDateData, selection: SearchParam): Promise<void> {
    const activityDay = this.setActivityDateByActivityData(activity);
    try {
      await this.navigateToSearchPage(activityDay, selection);
    } catch (error) {
      this.snackbar.open({ message: 'Something went wrong. Please try again.', duration: 5000 });
    }
  }

  /**
   * Opens a private note dialog for the specified result data.
   * 
   * @param resultData - Data related to the private note.
   */
  openPrivateNote(resultData: HotelResultData | ImageResultData, type: 'image' | 'hotel'): void {

    if (!this.store.selectSnapshot(UserState.authenticated)) {
      this.resetAuthHandle();
      const emitHandleKey = 'openPrivateNoteItineraryPage';
      this.store.selectSnapshot(AuthState.authenticated) || this.authDialog.open('login', emitHandleKey);
      this.appListener.globalSignalListener(emitHandleKey).pipe(
        tap(() => sessionStorage.removeItem(pendingAuthProcessKey)),
        takeUntil(this.authHandle$)
      ).subscribe(() => {
        const buttonClass = '.navbar--action-button';
        const button = document.body.querySelectorAll(buttonClass).item(1);
        button && button.dispatchEvent(new Event('click'));
      });
      setTimeout(() => this.navbarControl.navbarToolbarActionComplete$$.next(), 500);
      return;
    }

    const resultId = resultData?.id || '';
    const reservations = this.store.selectSnapshot(ItineraryState.itineraryReservations) || [];
    const reservation = reservations.find(e => e?.objId === resultId);

    this.composePrivateNote.open({
      dbId: this.store.selectSnapshot(ItineraryState.basicInfo)?.id,
      objId: reservation?.objId || resultData?.id,
      param: 'itinerary',
      objName: reservation?.objName || resultData?.name,
      location: reservation?.location || resultData?.address?.city,
      isBooked: reservation?.booked,
      checkIn: reservation?.checkIn,
      checkOut: reservation?.checkOut,
      exists: !!reservation, type,
      ...pick(reservation, [ 'notes', 'reference', 'contact' ])
    });
  }

  /**
   * Opens a read more dialog for the specified result data and type.
   * 
   * @param resultData - Data to be displayed in the dialog.
   * @param type - The type of dialog to open.
   */
  openReadMore(resultData: any, type: DialogType): void {
    this.searchResultDialog.open(resultData, type);
  }

  /**
   * Replace itinerary card.
   * 
   * @param resultData The data of the result item to be removed.
   * @param type The type of the result item, either 'image' or 'hotel'.
   * @param day The day of the itinerary from which to remove the item.
   */
  async replaceItinerary(resultData: SearchData, type: 'image' | 'hotel', day: number = 1, replaceChillTimeActivity?: boolean): Promise<void> {

    const activity = this.store.selectSnapshot(ItineraryState.actDateMap)?.[day] as ActivityDateData;

    if (!day || !activity) return;

    const selection = resultData?.type === 'hotel' ? 'hotels'
      : (<any>resultData)?.imgCategory?.toLowerCase()?.includes('food') ? 'foodanddrink'
      : 'thingstodo';

    try {
      this.setActivityDateByActivityData(activity);
      await this.navigateToSearchPage(activity, selection, (url: string) => {
        const method = replaceChillTimeActivity ? 'replaceChillTimeActivity$$' : 'replaceItineraryActivity$$';
        this.searchPageControl[method].next({
          composeUrl: url,
          idToBeRemoved: resultData.id,
          position: [ +resultData?.latitude, +resultData?.longitude ],
          type, day
        });
      });
    } catch (error) {
      this.snackbar.open({ message: 'Something went wrong. Please try again.', duration: 5000 });
    }
  }

  /**
   * Replace itinerary card.
   * 
   * @param resultData The data of the result item to be removed.
   * @param type The type of the result item, either 'image' or 'hotel'.
   * @param day The day of the itinerary from which to remove the item.
   */
  async addItineraryActivity(resultData: SearchData, day: number = 1): Promise<void> {
    this.replaceItinerary(resultData, 'image', day, true);
  }

  /**
   * Removes a result item from the itinerary.
   * 
   * @param resultData The data of the result item to be removed.
   * @param type The type of the result item, either 'image' or 'hotel'.
   * @param day The day of the itinerary from which to remove the item.
   */
  async removeResultItinerary(resultData: any, type: 'image' | 'hotel', day: number = 1): Promise<void> {

    if (resultData.imgCategory === 'Chill time') {
      return this.removeChillTimeResultItinerary(resultData, type, day, 'remove-item');
    }

    const chillTimeExecutable = () => this.removeChillTimeResultItinerary(resultData, type, day, 'remove-item-chill');
    
    const replaceEventExecutable = () => {
      if (!day) return;
      this.promptDialog.instance.close();
      this.replaceItinerary(resultData, type, day);
    }

    setTimeout(() => this.appListener.emitGlobalSignal('removeClickedProgress'), 300);
    this.promptDialog.open({
      title: 'Options',
      message: `compose.chill-time-another-activity`,
      buttons: [
        { name: 'compose.chill-time', theme: 'secondary', progressSpinner: true, executable: chillTimeExecutable },
        { name: 'compose.replace-event', theme: 'action', executable: replaceEventExecutable }
      ]
    });
  }

  /**
   * Removes a result item from the itinerary, marking it as "Chill time" or removing it entirely.
   * 
   * @param resultData The data of the result item to be removed.
   * @param type The type of the result item, either 'image' or 'hotel'.
   * @param day The day of the itinerary from which to remove the item.
   * @param action The action to perform, either 'remove-item-chill' or 'remove-item'.
   */
  private async removeChillTimeResultItinerary(resultData: any, type: 'image' | 'hotel', day: number, action: 'remove-item-chill' | 'remove-item'): Promise<void> {
    
    const dialog = this.promptDialog.reference;
    try {
      
      if (!day) return;

      this.promptDialogState.updateButtonState(0, true);
      this.searchPageControl.featureCardProcessing$$.next(true);
      dialog && (dialog.disableClose = true);
      action === 'remove-item' && this.navbarControl.navbarActionButtonState$$.next(false);
      action === 'remove-item' && this.stateService.dispatchPending$$.next(true);
      console.log('@@@ try:action', action);

      await firstValueFrom(this.store.dispatch(new ItineraryAction.UpdateItineraryActivity({
        action, day, type,
        param: this.itineraryType,
        item: resultData.id,
        id: this.store.selectSnapshot(ItineraryState.basicInfo)?.id || '',
        lang: currentLang(),
      })));

    } catch (response: any) {
      this.snackbar.open({ message: 'Something went wrong. Please try again.', duration: 5000 });
    } finally {
      this.appListener.emitGlobalSignal('removeClickedProgress');
      this.promptDialogState.updateButtonState(0, false);
      this.searchPageControl.featureCardProcessing$$.next(false);
      dialog && (dialog.disableClose = false);
      dialog && (dialog.close())
      action === 'remove-item' && this.navbarControl.navbarActionButtonState$$.next(true);
      action === 'remove-item' && this.stateService.dispatchPending$$.next(false);
      console.log('@@@ finally:action', action);
    }
  }

  findPlacesToStayNavigation(activity: ActivityDateData, index: number): void {

    getWindowRef()[preventRemoveKey] = '1';
    this.navigate(activity, 'hotels');

    setTimeout(() => {
      getWindowRef()[preventRemoveKey] = undefined;
    }, 3000);

  }

  async navigateToSearchPage(activity: ActivityDateData, selection: string, callback?: (url: string) => void): Promise<void> {

    const url = getWindowRef()?.location?.pathname;
    const selectedActCity = activity?.dayLocMap?.city;
    const routePath = [ currentLang(), selection, selectedActCity || '' ].filter(Boolean);
    const profileId = this.store.selectSnapshot(UserState.profileId) || '';

    if (selectedActCity && !activity?.dayLocMap?.id) {
      const brandLocations = this.store.selectSnapshot(BrandState.locations);
      const matchesCityOrAddress = (e: any, location: any) =>
        [e.city, e.formattedAddr].includes(location?.city) ||
        [e.city, e.formattedAddr].includes(location?.formatted_address);
      const brandLocationId = brandLocations?.find(e => matchesCityOrAddress(e, activity.dayLocMap))?.id;
      activity['dayLocMap']['id'] = brandLocationId || '';
    }

    getWindowRef()[preventUpdateFormStateByPlaceResult] = '1';

    callback && callback(url);

    activity?.dayLocMap && this.searchLocation.updateSearchLocation({
      name: selectedActCity || '',
      location: activity.dayLocMap?.formatted_address || '',
      locId: activity.dayLocMap?.id || '',
      latitude: +activity.dayLocMap?.longitude || 0,
      longitude: +activity.dayLocMap?.latitude || 0,
      type: activity?.dayLocMap?.type
    });

    const query = selection === 'thingstodo' ? getAdventuresQuery()
      : selection === 'hotels' ? getHotelsQuery()
      : selection === 'foodanddrink' ? getFoodsQuery() : getAdventuresQuery();

    setTimeout(async () => {
      if (query?.name !== selectedActCity) {
        await firstValueFrom(this.store.dispatch(new SearchAction.ResetSearchState([ 'adventures', 'hotels', 'foods' ])));
      }
      await firstValueFrom(this.store.dispatch(new SearchAction.ResetSearchState([ 'locationData' ])));

      if (selection === 'uploads') {
        this.router.navigate([ currentLang(), 'profile', profileId ]);
        this.store.select(ProfileState.state).pipe(
          filter(Boolean),
          take(1)
        ).subscribe(() => {
          this.router.navigate([ currentLang(), 'profile', profileId, 'uploads' ]);
        });
      } else {
        await this.router.navigate(routePath);
      }

      getWindowRef()[preventUpdateFormStateByPlaceResult] = undefined;
    }, 300);
  }

  resetAuthHandle(): void {
    this.authHandle$.next();
    sessionStorage.removeItem(pendingAuthProcessKey);
  }
  
  get composePage(): Observable<boolean> {
    return this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
      map(e => e.url.includes('/compose/')),
      startWith(this.router.url.includes('/compose/'))
    );
  }

  private setActivityDateByActivityData(activity: ActivityDateData): ActivityDate {
    const activityDay: any = { ...activity };
    activityDay['actDate'] = this.store.selectSnapshot(ItineraryState.actDate) || [];
    activityDay['actDateMap'] = this.store.selectSnapshot(ItineraryState.actDateMap) || [];
    activityDay['pageTitle'] = this.store.selectSnapshot(ItineraryState.basicInfo)?.pageTitle;
    activityDay['itineraryId'] = this.store.selectSnapshot(ItineraryState.basicInfo)?.id;
    this.searchPageControl.activityDate$$.next(activityDay);
    return activityDay;
  }

}
